import { type ChangeEvent, useEffect, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import stableHash from 'stable-hash';

import { saveSearchAction } from '@core/Actions/SavedSearch/SavedSearch.action';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { EMAIL_VALIDATION } from '@core/Constants/Validations';
import type { FilterValueDisplayObj } from '@core/Entities/Search/SearchFilters/FilterValueDisplayObj.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import { createQueryStrFromFilters } from '@core/Utils/Search/CreateQueryStrFromFilters';
import { getFilterValueDisplayName } from '@core/Utils/SearchFilters/GetFilterValueDisplayName';
import { getLabelWithEllipsis } from '@core/Utils/Strings/GetLabelWithEllipsis';
import { Button } from '@gds/Button/Button';
import { Chip } from '@gds/Chip/Chip';
import { Input } from '@gds/Input/Input';
import { Typography } from '@gds/Typography/Typography';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { PostSignup } from '@lead-forms/Components/LeadForm/StatusDisplays/PostSignup/PostSignup';
import { useModal } from '@modal/Hooks/UseModal';
import { userProfileAtom } from '@my-account/Atoms/UserProfile.atom';
import { useApiData } from '@my-account/Utils/CreateApiDataAtom';
import { StatusDisplay } from '@shared-components/StatusDisplay/StatusDisplay';
import { generateSavedSearchTrackingData } from '@tracking/SavedSearch.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { generateFiltersForDisplay } from '../../Utils/Filters/GenerateFiltersForDisplay';
import type { SavedSearchProps } from './SavedSearch.entity';

import styles from './SavedSearchForm.module.css';

export const SavedSearchForm = ({
  locale,
  strings,
  locationAttrs,
  allFilters,
}: SavedSearchProps) => {
  const aggregations = useAtomValue(aggregationsAtom);
  const userId = useAtomValue(userIdAtom);
  const flags = useAtomValue(flagsAtom);
  const { closeModal } = useModal();
  const { data: userProfile } = useApiData(userProfileAtom);
  const [formState, setFormState] = useAtom(formStateAtom);
  const [localFilters, setLocalFilters] = useState([...allFilters]);
  const localDisplayFilters = generateFiltersForDisplay(localFilters);

  const [hasFormSuccess, setHasFormSuccess] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState({
    value: userProfile?.email || '',
    isValid: true,
  });

  useEffect(() => {
    setFormState({ ...formState, email: email.value });
  }, [email.value]);

  const { createAlertConfirmation } = generateSavedSearchTrackingData({
    pageFilters: allFilters,
    userId,
  });

  const shouldShowSignup = !userProfile && flags.myAccount;

  const handleRemoveFilter = (deletedFltr: FilterValueDisplayObj) => {
    const filterChanges = deletedFltr.originalFilters.map(filterToRemove => {
      if (Array.isArray(deletedFltr.originalFilters[0].value)) {
        return {
          id: filterToRemove.id,
          value: deletedFltr.originalFilters[0].value.filter(value => value !== deletedFltr.value),
        };
      }

      return { id: filterToRemove.id, value: undefined };
    });

    const updatedFilters = localFilters.map(currentFltr => {
      const filterUpdate = filterChanges.find(removedFltr => currentFltr.id === removedFltr.id);
      return filterUpdate || currentFltr;
    });

    setLocalFilters(updatedFilters.filter(fltr => fltr.value !== undefined));
  };

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>, isBlur = false) => {
    const val = e.target.value;
    setEmail({
      value: val,
      isValid: isBlur ? EMAIL_VALIDATION.test(val) : true,
    });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email.value) {
      setEmail({
        ...email,
        isValid: false,
      });
      return false;
    }

    setIsLoading(true);
    const searchQ = createQueryStrFromFilters(localFilters);
    const plpUrl = createPathFromFilters(localFilters);
    const res = await saveSearchAction(email.value, searchQ, plpUrl, locale);

    if (res.error) {
      setHasFormSuccess(false);
    } else {
      setIsLoading(false);
      setHasFormSuccess(true);
    }
  };

  useEffect(() => {
    setLocalFilters([...allFilters]);
  }, [stableHash(allFilters)]);

  useEffect(() => {
    if (hasFormSuccess) {
      const { event, context } = createAlertConfirmation(email.value, localFilters);
      trackCustomEvent({ event, context });
    }
  }, [hasFormSuccess]);

  //show success / error
  if (hasFormSuccess !== undefined) {
    const msgs = {
      headline: hasFormSuccess
        ? strings.savedSearchSuccessHeadline
        : strings.savedSearchErrorHeadline,
      subheading: hasFormSuccess
        ? strings.savedSearchSuccessSubhead
        : strings.savedSearchErrorSubhead,
      description: hasFormSuccess ? strings.savedSearchSuccessDesc : strings.savedSearchErrorDesc,
    };
    return (
      <div className={styles.formSubmitted}>
        <StatusDisplay messages={msgs} type={hasFormSuccess ? 'success' : 'error'} leftAlignText />
        {shouldShowSignup && (
          <PostSignup strings={strings} variant="savedSearch" source="saved-search" />
        )}
        {!shouldShowSignup && (
          <Button onClick={() => closeModal(true)}>{strings.backToSearch}</Button>
        )}
      </div>
    );
  }

  return (
    <section className={styles.wrapper}>
      <Typography className={styles.title} variant={'h6'}>
        {strings.savedSearchStrapLine}
      </Typography>
      <div className={styles.colL}>
        <Typography variant={'subheading2'}>{strings.yourCriteria}</Typography>
        <div>
          {localDisplayFilters.map(filtr => {
            const canDelete = filtr.id !== 'postcode' && localDisplayFilters.length > 2;
            const deleteFn = canDelete ? () => handleRemoveFilter(filtr) : undefined;
            const valueId = `${(filtr.value || '').replace(/ /g, '')}${(
              filtr.valueGte || ''
            ).replace(/ /g, '')}${(filtr.valueLte || '').replace(/ /g, '')}`;

            const chipLabel = getFilterValueDisplayName(
              filtr,
              strings,
              aggregations,
              locationAttrs,
            );
            return (
              <Chip
                key={`${filtr.id}-${valueId}`}
                onDelete={deleteFn}
                variant="filter"
                id={`create-alert-chip-${filtr.id}-${valueId}`}
                dataTestId="create-alert-filter-chip"
                role="listitem"
                deleteLabel={strings.removeFromCriteria}
              >
                {getLabelWithEllipsis(chipLabel!, 30, 27)}
              </Chip>
            );
          })}
        </div>
      </div>
      <form id="saveSearchForm" className={styles.colR} onSubmit={onSubmit}>
        <Typography className={styles.formTitle} variant={'body1'}>
          {strings.savedSearchFormTitle}
        </Typography>
        <Input
          value={email.value}
          type={'email'}
          name="email"
          fullWidth
          label={strings.savedSearchInputLabel}
          onBlur={e => handleChangeEmail(e, true)}
          onChange={handleChangeEmail}
          className={styles.input}
          error={!email.isValid ? strings.savedSearchInputError : ''}
        />
        <input
          type="hidden"
          name="searchQuery"
          value={localDisplayFilters
            .map(filtr => {
              return filtr.originalFilters.map(origFiltr => `${origFiltr.id}=${origFiltr.value}, `);
            })
            .join('')}
        />
        <Typography className={styles.disclaimer} Component={'p'} variant={'caption5'}>
          {strings.savedSearchDisclaimer}{' '}
          <a href="/uk/privacy" target="_blank" rel="noopener">
            {strings.privacyPolicy}
          </a>
          {'.'}
        </Typography>
      </form>
      <div className={styles.ctaSection}>
        <Typography className={styles.ctaDisclaimer} Component={'p'} variant={'caption5'}>
          {strings.savedSearchSimpleDisclaimer}{' '}
          <a href="/uk/privacy" target="_blank" rel="noopener">
            {strings.privacyPolicy}
          </a>
          {'.'}
        </Typography>
        <Button type="submit" form="saveSearchForm" loading={isLoading} className={styles.formCta}>
          {strings.createAlert}
        </Button>
      </div>
    </section>
  );
};

SavedSearchForm.displayName = 'SavedSearchForm';
